import { CourseResponseDto, PRODUCT_KIND_LABELS, PRODUCT_STATE_LABELS } from '@repo/types';
import { UpdateProductDto } from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import { PRODUCT_KIND, PRODUCT_STATE } from '@theailab-dev/parchment';
import type { ProductKind, ProductState } from '@theailab-dev/parchment';
import type { Editor } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextAlignStart, TextAlignCenter, TextAlignEnd } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { useParams, useNavigate } from 'react-router-dom';
import { uploadContentImage } from '../../api/commonApi';
import { createProduct, updateProduct, checkSlugAvailability } from '../../api/productApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Card, Input, Button } from '../../components/ui';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CourseSelectModal from '../../components/ui/CourseSelectModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useRemoveWithConfirm } from '../../hooks';
import { useProductDetail } from '../../hooks/useProductDetail';
import { formatDateTimeLocal } from '../../utils/dateTime';
import { createContentImageEditorProps } from '@/utils/contentImageUpload';

/**
 * 이미지 URL을 절대 경로로 변환하는 헬퍼 함수
 */
const getImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_URL}${normalizedPath}`;
};

const ProductEdit: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = productId ? Number(productId) : null;
  const isEditMode = id !== null;

  const {
    data: product,
    isLoading,
    isError,
  } = useProductDetail(id || 0, {
    enabled: isEditMode,
  });

  // Form States

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState<ProductKind>(PRODUCT_KIND.SINGLE);
  const [state, setState] = useState<ProductState>(PRODUCT_STATE.NORMAL);
  const [openAt, setOpenAt] = useState('');
  const [closeAt, setCloseAt] = useState('');
  const [displayEndAt, setDisplayEndAt] = useState('');

  // 파일 업로드 관련 state
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  /** 썸네일 제거 */
  const removeThumbnail = useRemoveWithConfirm({
    mutationFn: (productId: number) => updateProduct(productId, { thumbnailUrl: null as unknown as string }),
    invalidateKeys: (productId) => [['product', productId]],
    title: '썸네일 제거',
    message: '썸네일 이미지를 제거하시겠습니까?',
  });
  /** 상세 이미지 제거 */
  const removeImage = useRemoveWithConfirm({
    mutationFn: (productId: number) => updateProduct(productId, { imageUrl: null as unknown as string }),
    invalidateKeys: (productId) => [['product', productId]],
    title: '상세 이미지 제거',
    message: '상세 이미지를 제거하시겠습니까?',
  });

  const [descriptionHtml, setDescriptionHtml] = useState('');

  const descriptionEditorRef = useRef<Editor | null>(null);

  // Tiptap 에디터 (상품 설명)
  // 편집 모드에서만 paste/drop 이미지 업로드 가능, 신규 생성 모드에서는 alert 표시
  const descriptionEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setDescriptionHtml(editor.getHTML());
    },
    editorProps: createContentImageEditorProps(descriptionEditorRef, (file) => {
      if (id == null) {
        alert('상품을 먼저 저장한 후 이미지를 추가할 수 있습니다.');
        return Promise.reject(new Error('Product not saved'));
      }
      return uploadContentImage(file, id);
    }),
  });

  useEffect(() => {
    descriptionEditorRef.current = descriptionEditor ?? null;
  }, [descriptionEditor]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  // 단일 상품용 강의 선택 관련 state
  const [selectedCourse, setSelectedCourse] = useState<CourseResponseDto | null>(null);
  const [selectedProductItemId, setSelectedProductItemId] = useState<number | null>(null);
  const [coursePrice, setCoursePrice] = useState(''); // product_item.original_price
  const [packageDiscount, setPackageDiscount] = useState(''); // product_package.discount (상품 패키지 할인가)
  const [showCourseSelectModal, setShowCourseSelectModal] = useState(false);

  // 슬러그 검증 관련 state
  const [slugValidation, setSlugValidation] = useState<{
    isChecking: boolean;
    isAvailable: boolean | null;
    message: string;
  }>({
    isChecking: false,
    isAvailable: null,
    message: '',
  });

  // 알림 모달 상태
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'error' | 'warning' | 'success' | 'info';
    title: string;
    message: string;
    onClose?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // 확인 모달 상태 (유형 변경 등)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 알림 표시 헬퍼 함수
  const showAlert = (
    type: 'error' | 'warning' | 'success' | 'info',
    title: string,
    message: string,
    onClose?: () => void,
  ) => {
    setAlertModal({ isOpen: true, type, title, message, onClose });
  };

  // Load data into form
  useEffect(() => {
    if (product) {
      setTitle(product.title);
      setSubtitle(product.subtitle || '');
      setSlug(product.slug);

      setKind(product.kind);

      setState(product.state as ProductState);
      setOpenAt(formatDateTimeLocal(product.openAt));
      setCloseAt(formatDateTimeLocal(product.closeAt));
      setDisplayEndAt(formatDateTimeLocal(product.displayEndAt));
    }
  }, [product]);

  // 에디터 내용 업데이트
  useEffect(() => {
    if (descriptionEditor && product) {
      descriptionEditor.commands.setContent(product.description || '');
      setDescriptionHtml(product.description || '');
    }
  }, [descriptionEditor, product]);

  // 편집 모드에서는 상품 아이템 정보를 로드하지 않음 (상품 상세 화면에서만 관리)
  // 생성 모드에서만 강의 선택을 위한 초기화는 필요 없음

  // 슬러그 실시간 검증 (debounce)
  useEffect(() => {
    // 빈 값이거나 형식이 맞지 않으면 검증하지 않음
    if (!slug.trim()) {
      setSlugValidation({
        isChecking: false,
        isAvailable: null,
        message: '',
      });
      return;
    }

    // 슬러그 형식 검증
    const slugPattern = /^[a-z0-9_-]+$/;
    if (!slugPattern.test(slug)) {
      setSlugValidation({
        isChecking: false,
        isAvailable: false,
        message: '영문 소문자, 숫자, 하이픈(-), 언더스코어(_)만 사용할 수 있습니다.',
      });
      return;
    }

    // 수정 모드에서 현재 슬러그와 동일하면 검증하지 않음
    if (isEditMode && product && product.slug === slug) {
      setSlugValidation({
        isChecking: false,
        isAvailable: true,
        message: '',
      });
      return;
    }

    // debounce: 500ms 후 검증
    const timeoutId = setTimeout(async () => {
      setSlugValidation({
        isChecking: true,
        isAvailable: null,
        message: '확인 중...',
      });

      try {
        const result = await checkSlugAvailability(slug);
        setSlugValidation({
          isChecking: false,
          isAvailable: result.available,
          message: result.available ? '사용 가능한 슬러그입니다.' : '이미 사용 중인 슬러그입니다.',
        });
      } catch (error) {
        // API 에러 발생 시 검증하지 않음 (백엔드에서 최종 검증)
        console.error('슬러그 중복 확인 실패:', error);
        setSlugValidation({
          isChecking: false,
          isAvailable: null,
          message: '',
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [slug, id, isEditMode, product]);

  /**
   * 썸네일 파일 선택 핸들러
   */
  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const imageUrl = URL.createObjectURL(file);
      setThumbnailPreview(imageUrl);
    }
  };

  /**
   * 이미지 파일 선택 핸들러
   */
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
    }
  };

  /**
   * 썸네일 파일 제거 핸들러
   */
  const handleRemoveThumbnailFile = () => {
    setThumbnailFile(null);
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
    setThumbnailPreview(null);
    const fileInput = document.getElementById('thumbnailFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 이미지 파일 제거 핸들러
   */
  const handleRemoveImageFile = () => {
    setImageFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    const fileInput = document.getElementById('imageFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // 미리보기 URL 정리
  useEffect(() => {
    return () => {
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [thumbnailPreview, imagePreview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const description = descriptionEditor?.getHTML() || undefined;

      // 기본 필수 필드 검증 (NOT NULL 필드)

      // title: NOT NULL
      // 1차 방어: 단일 상품이고 강의가 선택되어 있으면 강의 제목으로 자동 채우기
      let finalTitle = title.trim();
      if (!finalTitle && kind === PRODUCT_KIND.SINGLE && selectedCourse) {
        finalTitle = selectedCourse.title;
        setTitle(finalTitle);
      }

      if (!finalTitle) {
        showAlert('error', '입력 오류', '상품명을 입력해주세요.');
        setIsSubmitting(false);
        return;
      }

      // slug: NOT NULL, UNIQUE
      if (!slug.trim()) {
        showAlert('error', '입력 오류', '상품 슬러그를 입력해주세요.');
        setIsSubmitting(false);
        return;
      }

      // 슬러그 형식 검증 (영문, 숫자, 하이픈, 언더스코어만 허용)
      const slugPattern = /^[a-z0-9_-]+$/;
      if (!slugPattern.test(slug)) {
        showAlert('error', '입력 오류', '슬러그는 영문 소문자, 숫자, 하이픈(-), 언더스코어(_)만 사용할 수 있습니다.');
        setIsSubmitting(false);
        return;
      }

      // 슬러그 중복 검증 (실시간 검증 결과 확인)
      if (slugValidation.isAvailable === false) {
        showAlert('error', '입력 오류', '이미 사용 중인 슬러그입니다. 다른 슬러그를 입력해주세요.');
        setIsSubmitting(false);
        return;
      }

      // 슬러그 검증 중이면 대기
      if (slugValidation.isChecking) {
        showAlert('info', '확인 중', '슬러그 중복 확인 중입니다. 잠시 후 다시 시도해주세요.');
        setIsSubmitting(false);
        return;
      }

      // kind: NOT NULL

      if (!kind || !Object.values(PRODUCT_KIND).includes(kind)) {
        showAlert('error', '입력 오류', '상품 유형을 선택해주세요.');

        setIsSubmitting(false);
        return;
      }

      // state: NOT NULL (기본값 'NORMAL'이지만 명시적으로 설정)

      if (!state || !Object.values(PRODUCT_STATE).includes(state)) {
        showAlert('error', '입력 오류', '상품 상태를 선택해주세요.');

        setIsSubmitting(false);
        return;
      }

      // 판매 기간 검증 (종료일이 시작일보다 앞서면 안됨) - 문제 6번
      if (openAt && closeAt) {
        const start = new Date(openAt);
        const end = new Date(closeAt);
        if (end < start) {
          showAlert('error', '입력 오류', '판매 종료일은 판매 시작일보다 이후여야 합니다.');
          setIsSubmitting(false);
          return;
        }
      }

      if (isEditMode && id) {
        // 수정 모드: 유형 변경 검증
        // 백엔드에서 자동으로 정리하므로 프론트엔드 검증 제거
        // if (kind === "SINGLE" && product && product.items.length > 1) {
        //   showAlert("warning", "유형 변경 불가", "단일 상품으로 변경하려면 등록된 강의가 1개 이하여야 합니다.\n현재 등록된 강의를 삭제한 후 다시 시도해주세요.");
        //   setIsSubmitting(false);
        //   return;
        // }

        // 변경 사항 감지
        if (!product) {
          showAlert('error', '오류', '상품 정보를 불러올 수 없습니다.');
          setIsSubmitting(false);
          return;
        }

        const hasChanges =
          title !== product!.title ||
          subtitle !== (product!.subtitle || '') ||
          slug !== product!.slug ||
          description !== (product!.description || undefined) ||
          kind !== product!.kind ||
          state !== product!.state ||
          openAt !== formatDateTimeLocal(product!.openAt) ||
          closeAt !== formatDateTimeLocal(product!.closeAt) ||
          displayEndAt !== formatDateTimeLocal(product!.displayEndAt) ||
          thumbnailFile !== null ||
          imageFile !== null;

        if (!hasChanges) {
          showAlert('info', '변경 사항 없음', '수정된 내용이 없습니다.');
          setIsSubmitting(false);
          return;
        }

        const updateData: UpdateProductDto = {
          title: finalTitle,
          subtitle: subtitle.trim() || null,
          slug,
          description,
          kind,
          state,
          openAt: openAt ? new Date(openAt) : null,
          closeAt: closeAt ? new Date(closeAt) : null,
          displayEndAt: displayEndAt ? new Date(displayEndAt) : null,
        };

        await updateProduct(id, updateData, thumbnailFile || undefined, imageFile || undefined);

        // 수정 모드에서는 상품 아이템 편집을 하지 않음 (상품 상세 화면에서만 가능)

        // 상품 상세 캐시 무효화하여 변경사항 즉시 반영
        await queryClient.invalidateQueries({ queryKey: ['products'] });
        await queryClient.invalidateQueries({ queryKey: ['product', id] });

        showAlert('success', '수정 완료', '상품이 수정되었습니다.', () => {
          navigate(`/dashboard/products/${id}`);
        });
      } else {
        // 생성 모드
        // 단일 상품인 경우 강의 선택 필수
        if (kind === PRODUCT_KIND.SINGLE && !selectedCourse) {
          showAlert(
            'error',
            '입력 오류',
            "단일 상품은 연결할 강의를 선택해야 합니다.\n'연결 강의' 섹션에서 강의를 검색하여 선택해주세요.",
          );
          setIsSubmitting(false);
          return;
        }

        // 단일 상품인 경우 가격 입력 필수 및 유효성 검증
        if (kind === PRODUCT_KIND.SINGLE && selectedCourse) {
          // 원가 검증
          if (!coursePrice.trim()) {
            showAlert('error', '입력 오류', '원가를 입력해주세요.');
            setIsSubmitting(false);
            return;
          }

          const price = parseFloat(coursePrice);
          if (isNaN(price)) {
            showAlert('error', '입력 오류', '원가는 숫자로 입력해주세요.');
            setIsSubmitting(false);
            return;
          }

          if (price < 0) {
            showAlert('error', '입력 오류', '원가는 0 이상의 값으로 입력해주세요.');
            setIsSubmitting(false);
            return;
          }

          // 할인가 검증
          const discountValue = packageDiscount.trim() ? parseFloat(packageDiscount) : 0;

          if (packageDiscount.trim() && isNaN(discountValue)) {
            showAlert('error', '입력 오류', '할인가는 숫자로 입력해주세요.');
            setIsSubmitting(false);
            return;
          }

          if (discountValue < 0) {
            showAlert('error', '입력 오류', '할인가는 0 이상의 값으로 입력해주세요.');
            setIsSubmitting(false);
            return;
          }

          // 할인가가 원가보다 큰지 확인 (같으면 0원 상품 -> 허용) - 문제 2번
          if (discountValue > price) {
            showAlert(
              'error',
              '입력 오류',
              `할인가는 원가(${price.toLocaleString()}원)보다 클 수 없습니다.\n현재 입력된 할인가: ${discountValue.toLocaleString()}원`,
            );
            setIsSubmitting(false);
            return;
          }
        }

        const createData: any = {
          title: finalTitle,
          subtitle: subtitle.trim() || null,
          slug,
          description,
          kind,

          state,

          openAt: openAt ? new Date(openAt) : null,
          closeAt: closeAt ? new Date(closeAt) : null,
          displayEndAt: displayEndAt ? new Date(displayEndAt) : null,
          productItemId:
            kind === PRODUCT_KIND.SINGLE && selectedProductItemId ? Number(selectedProductItemId) : undefined,
          // 문제 3번: 할인 금액 정보 추가
          discount: kind === PRODUCT_KIND.SINGLE ? Number(packageDiscount || 0) : 0,
        };

        // 단일 상품이고 강의가 선택된 경우 해당 강의와 연결된 productItemId를 전송
        if (kind === PRODUCT_KIND.SINGLE && selectedCourse) {
          if (!selectedProductItemId || selectedProductItemId <= 0) {
            showAlert('error', '입력 오류', '상품 아이템을 선택해주세요.');
            setIsSubmitting(false);
            return;
          }
          createData.productItemId = Number(selectedProductItemId);
          console.log('✅ 상품 생성에 포함할 productItemId:', createData.productItemId);
        }

        console.log('📤 상품 생성 요청 데이터:', createData);

        const newProduct = await createProduct(createData, thumbnailFile || undefined, imageFile || undefined);

        // 페이지 이동 후 상세 페이지에서 자동으로 최신 데이터를 로드하므로 invalidateQueries 불필요

        showAlert('success', '생성 완료', '상품이 생성되었습니다.', () => {
          navigate(`/dashboard/products/${newProduct.id}`);
        });
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} product:`, error);
      showAlert('error', '처리 실패', `상품 ${isEditMode ? '수정' : '생성'}에 실패했습니다.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <DetailPageLayout
        title="상품 수정"
        onBack={() => navigate(id ? `/dashboard/products/${id}` : '/dashboard/products')}>
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DetailPageLayout>
    );
  }

  if (isEditMode && (isError || !product)) {
    return (
      <DetailPageLayout
        title="상품 수정"
        onBack={() => navigate(id ? `/dashboard/products/${id}` : '/dashboard/products')}>
        <div className="flex h-64 flex-col items-center justify-center">
          <p className="text-red-500">상품 정보를 불러오는데 실패했습니다.</p>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <>
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-8 shadow-xl">
            <div className="text-center">
              <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
              <h3 className="mb-2 text-lg font-semibold text-slate-900">
                {isEditMode ? '상품 수정 중...' : '상품 생성 중...'}
              </h3>
              <p className="text-sm text-slate-600">
                {thumbnailFile || imageFile
                  ? '파일을 업로드하고 있습니다. 잠시만 기다려주세요.'
                  : '상품 정보를 저장하고 있습니다.'}
              </p>
            </div>
          </div>
        </div>
      )}
      <DetailPageLayout
        title={isEditMode ? '상품 수정' : '새 상품 만들기'}
        onBack={() => navigate(isEditMode && id ? `/dashboard/products/${id}` : '/dashboard/products')}
        actions={
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isEditMode ? '저장' : '생성'}
          </Button>
        }>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column: Main Info */}
            <div className="space-y-6 lg:col-span-2">
              <Card title="기본 정보">
                <div className="space-y-4">
                  <Input
                    id="title"
                    label="상품명"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => {
                      // 1차 방어: 포커스를 잃을 때 title이 비어있고 단일 상품이고 강의가 선택되어 있으면 자동으로 채우기
                      if (!title.trim() && kind === PRODUCT_KIND.SINGLE && selectedCourse) {
                        setTitle(selectedCourse.title);
                      }
                    }}
                    placeholder={kind === PRODUCT_KIND.SINGLE && selectedCourse ? selectedCourse.title : undefined}
                    required
                  />
                  <Input
                    id="subtitle"
                    label="부제목"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="상품 부제목을 입력하세요"
                  />
                  <div>
                    <Input
                      id="slug"
                      label="슬러그 (URL 식별자)"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      required
                      className={
                        slugValidation.isAvailable === false
                          ? 'border-red-300 focus:ring-red-500'
                          : slugValidation.isAvailable === true
                            ? 'border-green-300 focus:ring-green-500'
                            : ''
                      }
                    />
                    {/* 슬러그 검증 메시지 */}
                    {slugValidation.message && (
                      <div
                        className={`mt-1.5 flex items-center gap-1.5 text-sm ${
                          slugValidation.isChecking
                            ? 'text-slate-500'
                            : slugValidation.isAvailable === false
                              ? 'text-red-600'
                              : slugValidation.isAvailable === true
                                ? 'text-green-600'
                                : ''
                        }`}>
                        {slugValidation.isChecking ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                            <span>{slugValidation.message}</span>
                          </>
                        ) : (
                          <>
                            {slugValidation.isAvailable === false ? (
                              <svg
                                className="h-4 w-4 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            ) : slugValidation.isAvailable === true ? (
                              <svg
                                className="h-4 w-4 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : null}
                            <span>{slugValidation.message}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">상품 설명</label>
                    {!isEditMode && (
                      <div className="mb-2 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <HiInformationCircle className="mt-0.5 flex-shrink-0 text-blue-500" size={18} />
                        <p className="text-sm text-blue-700">
                          상품을 먼저 저장한 후 에디터에 이미지를 추가할 수 있습니다.
                        </p>
                      </div>
                    )}
                    {descriptionEditor && (
                      <>
                        <div className="overflow-hidden rounded-lg border border-slate-300 focus-within:border-transparent focus-within:ring-2 focus-within:ring-indigo-500">
                          {/* 툴바 */}
                          <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-2">
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleBold().run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('bold') ? 'bg-slate-300' : ''
                              }`}>
                              <strong>B</strong>
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleItalic().run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('italic') ? 'bg-slate-300' : ''
                              }`}>
                              <em>I</em>
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleHeading({ level: 1 }).run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('heading', {
                                  level: 1,
                                })
                                  ? 'bg-slate-300'
                                  : ''
                              }`}>
                              H1
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('heading', {
                                  level: 2,
                                })
                                  ? 'bg-slate-300'
                                  : ''
                              }`}>
                              H2
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleBulletList().run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('bulletList') ? 'bg-slate-300' : ''
                              }`}>
                              •
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().toggleOrderedList().run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('orderedList') ? 'bg-slate-300' : ''
                              }`}>
                              1.
                            </button>
                            <div className="mx-1 h-6 w-px bg-slate-300" />
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().setTextAlign('left').run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive({ textAlign: 'left' }) ? 'bg-slate-300' : ''
                              }`}
                              title="왼쪽 정렬">
                              <TextAlignStart className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().setTextAlign('center').run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive({ textAlign: 'center' }) ? 'bg-slate-300' : ''
                              }`}
                              title="가운데 정렬">
                              <TextAlignCenter className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => descriptionEditor.chain().focus().setTextAlign('right').run()}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive({ textAlign: 'right' }) ? 'bg-slate-300' : ''
                              }`}
                              title="오른쪽 정렬">
                              <TextAlignEnd className="h-4 w-4" />
                            </button>
                            <div className="mx-1 h-6 w-px bg-slate-300" />
                            <button
                              type="button"
                              onClick={() => {
                                const url = window.prompt('링크 URL을 입력하세요:');
                                if (url) {
                                  descriptionEditor.chain().focus().setLink({ href: url }).run();
                                }
                              }}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                descriptionEditor.isActive('link') ? 'bg-slate-300' : ''
                              }`}>
                              링크
                            </button>
                            <div className="mx-1 h-6 w-px bg-slate-300" />
                            <button
                              type="button"
                              onClick={() => setShowHtmlSource(!showHtmlSource)}
                              className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                                showHtmlSource ? 'bg-slate-300' : ''
                              }`}>
                              HTML
                            </button>
                          </div>
                          {/* 에디터 또는 HTML 소스 */}
                          {showHtmlSource ? (
                            <textarea
                              value={descriptionHtml}
                              onChange={(e) => {
                                setDescriptionHtml(e.target.value);
                                descriptionEditor.commands.setContent(e.target.value);
                              }}
                              className="min-h-[200px] w-full resize-y border-none p-4 font-mono text-sm outline-none"
                              placeholder="HTML 코드를 입력하세요..."
                            />
                          ) : (
                            <EditorContent
                              editor={descriptionEditor}
                              className="prose prose-sm min-h-[200px] max-w-none p-4 focus:outline-none"
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column: Settings */}
            <div className="space-y-6">
              <Card title="이미지 설정">
                <div className="space-y-6">
                  {/* 썸네일 업로드 */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">썸네일</label>
                    <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
                      썸네일과 상세 이미지가 동일하다면, 상세 이미지만 등록하세요.
                    </p>
                    {thumbnailPreview ? (
                      <div className="space-y-3">
                        <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                          <img src={thumbnailPreview} alt="썸네일 미리보기" className="h-full w-full object-cover" />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => document.getElementById('thumbnailFile')?.click()}
                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
                            변경
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveThumbnailFile}
                            className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300">
                            제거
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {product?.thumbnailUrl && id && (
                          <div className="space-y-2">
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                              <img
                                src={getImageUrl(product.thumbnailUrl) || ''}
                                alt="현재 썸네일"
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                            <p className="text-xs text-slate-500">
                              현재 썸네일. 새로운 파일을 선택하면 기존 이미지를 대체합니다.
                            </p>
                            <button
                              type="button"
                              onClick={removeThumbnail.openConfirm}
                              disabled={removeThumbnail.isPending}
                              className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50">
                              {removeThumbnail.isPending ? '처리 중...' : '썸네일 제거'}
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          id="thumbnailFile"
                          name="thumbnailFile"
                          accept="image/*"
                          onChange={handleThumbnailFileChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('thumbnailFile')?.click()}
                          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                          파일 선택
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 이미지 업로드 */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">상세 이미지</label>
                    {imagePreview ? (
                      <div className="space-y-3">
                        <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                          <img src={imagePreview} alt="이미지 미리보기" className="h-full w-full object-cover" />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => document.getElementById('imageFile')?.click()}
                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
                            변경
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveImageFile}
                            className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300">
                            제거
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {product?.imageUrl && id && (
                          <div className="space-y-2">
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                              <img
                                src={getImageUrl(product.imageUrl) || ''}
                                alt="현재 이미지"
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                            <p className="text-xs text-slate-500">
                              현재 이미지. 새로운 파일을 선택하면 기존 이미지를 대체합니다.
                            </p>
                            <button
                              type="button"
                              onClick={removeImage.openConfirm}
                              disabled={removeImage.isPending}
                              className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50">
                              {removeImage.isPending ? '처리 중...' : '상세 이미지 제거'}
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          id="imageFile"
                          name="imageFile"
                          accept="image/*"
                          onChange={handleImageFileChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('imageFile')?.click()}
                          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                          파일 선택
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
              <Card title="설정">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">유형</label>
                    <select
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={kind}
                      onChange={(e) => {
                        const newKind = e.target.value as ProductKind;

                        // SINGLE로 변경 시 아이템 정리 로직

                        if (newKind === PRODUCT_KIND.SINGLE && product && product.items.length > 1) {
                          setConfirmModal({
                            isOpen: true,
                            title: '유형 변경 확인',
                            message:
                              '단일 상품은 하나의 강의만 연결할 수 있습니다.\n변경 시 첫 번째 아이템을 제외한 나머지 아이템 연결이 해제됩니다.\n계속하시겠습니까?',
                            onConfirm: () => {
                              setKind(newKind);
                              setConfirmModal((prev) => ({
                                ...prev,
                                isOpen: false,
                              }));
                            },
                          });
                          return;
                        }

                        setKind(newKind);
                        // 유형 변경 시 선택된 강의 초기화
                        if (newKind === PRODUCT_KIND.ROADMAP) {
                          setSelectedCourse(null);
                          setCoursePrice('');
                          setPackageDiscount('');
                        }
                      }}>
                      {Object.values(PRODUCT_KIND).map((k) => (
                        <option key={k} value={k}>
                          {PRODUCT_KIND_LABELS[k]}
                        </option>
                      ))}
                    </select>

                    {/* 로드맵 선택 시 안내 문구 */}
                    {kind === PRODUCT_KIND.ROADMAP && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <HiInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />

                        <p className="text-sm text-blue-800">
                          로드맵에 포함될 강의는 '저장' 후 다음 단계에서 설정할 수 있습니다.
                        </p>
                      </div>
                    )}

                    {/* 단일 상품 선택 시 강의 선택 UI (생성 모드만) */}
                    {kind === PRODUCT_KIND.SINGLE && !isEditMode && (
                      <div className="mt-4 space-y-3">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">연결 강의</label>
                          {selectedCourse ? (
                            <div className="space-y-2">
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{selectedCourse.title}</p>
                                    <p className="mt-0.5 text-xs text-slate-500">
                                      {selectedCourse.instructor?.name || '강사 미정'}
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowCourseSelectModal(true)}>
                                    변경
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">원가 (원)</label>
                                  <Input
                                    type="number"
                                    placeholder="강의 선택 시 자동 입력"
                                    value={coursePrice}
                                    readOnly
                                    disabled
                                    className="cursor-not-allowed bg-slate-100 text-slate-500"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">할인가 (원)</label>
                                  <Input
                                    type="number"
                                    placeholder="예: 10000"
                                    value={packageDiscount}
                                    onChange={(e) => setPackageDiscount(e.target.value)}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    min="0"
                                    step="1000"
                                  />
                                </div>
                              </div>
                              {coursePrice && packageDiscount && (
                                <p className="text-xs text-slate-500">
                                  최종 가격:{' '}
                                  <span className="font-semibold text-slate-900">
                                    {(parseFloat(coursePrice) - (parseFloat(packageDiscount) || 0)).toLocaleString()}원
                                  </span>
                                </p>
                              )}
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowCourseSelectModal(true)}
                              className="w-full">
                              강의 검색 및 선택
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 수정 모드: 단일 상품인 경우 안내 메시지 */}

                    {kind === PRODUCT_KIND.SINGLE && isEditMode && (
                      <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <HiInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />

                        <p className="text-sm text-blue-800">
                          연결 강의 및 가격 정보는 상품 상세 화면에서 수정할 수 있습니다.
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">상태</label>
                    <select
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={state}
                      onChange={(e) => setState(e.target.value as ProductState)}>
                      {Object.values(PRODUCT_STATE).map((s) => (
                        <option key={s} value={s}>
                          {PRODUCT_STATE_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Card>

              <Card title="판매 설정">
                <div className="space-y-4">
                  <Input
                    id="openAt"
                    type="datetime-local"
                    label="판매 시작일"
                    value={openAt}
                    onChange={(e) => setOpenAt(e.target.value)}
                  />
                  <Input
                    id="closeAt"
                    type="datetime-local"
                    label="판매 종료일"
                    value={closeAt}
                    onChange={(e) => setCloseAt(e.target.value)}
                  />
                  <Input
                    id="displayEndAt"
                    type="datetime-local"
                    label="노출 종료일"
                    value={displayEndAt}
                    onChange={(e) => setDisplayEndAt(e.target.value)}
                  />
                </div>
              </Card>
            </div>
          </div>
        </form>

        {/* 강의 선택 모달 (단일 상품용) */}
        <CourseSelectModal
          isOpen={showCourseSelectModal}
          onClose={() => setShowCourseSelectModal(false)}
          onSelect={(course) => {
            setSelectedCourse(course);
            // @ts-ignore: price might exist in runtime response
            setCoursePrice(course.price !== undefined && course.price !== null ? course.price.toString() : '');
            // @ts-ignore: productItemId might exist in runtime response
            const productItemId = course.productItemId;
            console.log('🔍 선택된 강의의 productItemId:', {
              courseId: course.id,
              courseTitle: course.title,
              productItemId,
              productItemIdType: typeof productItemId,
            });
            if (productItemId && Number(productItemId) > 0) {
              setSelectedProductItemId(Number(productItemId));
            } else {
              setSelectedProductItemId(null);
              console.warn('⚠️ 유효하지 않은 productItemId:', productItemId);
            }
            // title이 비어있으면 강의 제목으로 자동 채우기
            if (!title.trim()) {
              setTitle(course.title);
            }
            setShowCourseSelectModal(false);
          }}
        />

        <ConfirmModal
          isOpen={alertModal.isOpen}
          onClose={() => {
            setAlertModal({ ...alertModal, isOpen: false });
            if (alertModal.onClose) alertModal.onClose();
          }}
          type={alertModal.type}
          title={alertModal.title}
          message={alertModal.message}
        />

        {/* 확인 모달 (유형 변경 등) */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          title={confirmModal.title}
          message={confirmModal.message}
          type="warning"
          buttons={[
            {
              label: '취소',
              onClick: () => setConfirmModal({ ...confirmModal, isOpen: false }),
              variant: 'secondary',
            },
            {
              label: '확인',
              onClick: confirmModal.onConfirm,
              variant: 'danger',
            },
          ]}
        />

        {id && (
          <>
            <ConfirmModal {...removeThumbnail.getConfirmModalProps(Number(id))} />
            <ConfirmModal {...removeImage.getConfirmModalProps(Number(id))} />
          </>
        )}
      </DetailPageLayout>
    </>
  );
};

export default ProductEdit;
